// TrainingAttendeeList.js
// Read-only view of a training's attendance/assessment data, for reviewing
// after the fact — a separate presentation of the same data the trainer's
// editable entry form (TrainingAttendance.js) already captures, not a new
// data model.

import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Table, Button, Form } from 'react-bootstrap';
import { computeAttendanceStats } from '../utils/attendanceStats';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function sortIndicator(sortConfig, key) {
  if (sortConfig.key !== key) return '';
  return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
}

function TrainingAttendeeList() {
  const { id: trainingId } = useParams();
  const [attendees, setAttendees] = useState([]);
  const [training, setTraining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [statusFilter, setStatusFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');

  useEffect(() => {
    fetch(`${API_BASE_URL}/trainings/get_training_attendance.php?id=${trainingId}&detail=1`, {
      credentials: 'include',
    })
      .then(res => res.json())
      .then(data => setAttendees(Array.isArray(data) ? data : []))
      .catch(error => console.error('Error fetching attendee list:', error))
      .finally(() => setLoading(false));
  }, [trainingId]);

  // Same self-fetch pattern as the Attendance/Participants tabs
  // (TrainingAttendance.js / TrainingParticipants.js) — this component only
  // ever receives trainingId via the route, not a training object as a prop.
  useEffect(() => {
    fetch(`${API_BASE_URL}/trainings/get_training.php?id=${trainingId}`, {
      credentials: 'include',
    })
      .then(res => res.json())
      .then(data => setTraining(data && !data.message ? data : null))
      .catch(error => console.error('Error fetching training details:', error));
  }, [trainingId]);

  // Department filter options are derived from this training's own roster,
  // not a separate departments API call — no new data source needed.
  const departments = useMemo(() => {
    return [...new Set(attendees.map(a => a.department).filter(Boolean))].sort();
  }, [attendees]);

  const filtered = useMemo(() => {
    return attendees.filter(a => {
      if (statusFilter === 'attended' && Number(a.attended) !== 1) return false;
      if (statusFilter === 'not_attended' && Number(a.attended) === 1) return false;
      if (departmentFilter && a.department !== departmentFilter) return false;
      return true;
    });
  }, [attendees, statusFilter, departmentFilter]);

  const sorted = useMemo(() => {
    const getValue = (row) => {
      switch (sortConfig.key) {
        case 'department': return row.department || '';
        case 'status': return Number(row.attended);
        case 'result': return row.result_1 || '';
        default: return row.name || '';
      }
    };
    return [...filtered].sort((a, b) => {
      const aVal = getValue(a);
      const bVal = getValue(b);
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filtered, sortConfig]);

  // Always over the full roster, not whatever's currently filtered — this
  // is a training-level summary, not a "count of what's on screen" figure.
  const stats = useMemo(() => computeAttendanceStats(attendees), [attendees]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const exportUrl = `${API_BASE_URL}/trainings/get_training_attendance.php?id=${trainingId}&format=csv`;

  if (loading) {
    return <p>Loading attendee list...</p>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h4 className="mb-2">Training Attendee List</h4>
          {training && (
            <div className="mb-2">
              <div><strong>Course:</strong> {training.code} — {training.title}</div>
              <div><strong>Date & Time:</strong> {training.start_date} to {training.end_date}, {training.startTime?.slice(0, 5)} - {training.endTime?.slice(0, 5)}</div>
              <div><strong>Location/Venue:</strong> {training.location || '-'}</div>
              <div><strong>Trainer:</strong> {training.assignedTrainer || '-'}</div>
              <div><strong>Training Vendor/Company:</strong> {training.vendor || '-'}</div>
            </div>
          )}
          <div className="text-muted small">Date Printed: {new Date().toLocaleDateString()}</div>
        </div>
        <div className="no-print">
          <Button variant="outline-secondary" className="me-2" onClick={() => window.print()}>
            Print
          </Button>
          <Button as="a" href={exportUrl} variant="outline-primary">
            Export to CSV
          </Button>
        </div>
      </div>

      <div className="mb-3">
        <div><strong>Attendance Rate:</strong> {stats.attendanceRate !== null ? `${stats.attendanceRate.toFixed(1)}% (${stats.attendedCount}/${stats.total})` : '-'}</div>
        <div><strong>Average Result 1:</strong> {stats.avgResult1 !== null ? stats.avgResult1.toFixed(1) : 'N/A (no numeric entries)'}</div>
        <div><strong>Average Result 2:</strong> {stats.avgResult2 !== null ? stats.avgResult2.toFixed(1) : 'N/A (no numeric entries)'}</div>
      </div>

      <div className="d-flex gap-3 no-print mb-3">
        <Form.Group>
          <Form.Label>Attendance Status</Form.Label>
          <Form.Control as="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            <option value="attended">Attended</option>
            <option value="not_attended">Not Attended</option>
          </Form.Control>
        </Form.Group>
        <Form.Group>
          <Form.Label>Department</Form.Label>
          <Form.Control as="select" value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
            <option value="">All Departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </Form.Control>
        </Form.Group>
      </div>

      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>Name{sortIndicator(sortConfig, 'name')}</th>
            <th>Designation</th>
            <th>Company</th>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('department')}>Department{sortIndicator(sortConfig, 'department')}</th>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>Attendance Status{sortIndicator(sortConfig, 'status')}</th>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('result')}>Result 1{sortIndicator(sortConfig, 'result')}</th>
            <th>Result 2</th>
          </tr>
        </thead>
        <tbody>
          {sorted.length > 0 ? sorted.map(a => (
            <tr key={a.user_id}>
              <td>{a.name}</td>
              <td>{a.designation}</td>
              <td>{a.company}</td>
              <td>{a.department}</td>
              <td>{Number(a.attended) === 1 ? 'Attended' : 'Not Attended'}</td>
              <td>{a.result_1}</td>
              <td>{a.result_2}</td>
            </tr>
          )) : (
            <tr>
              <td colSpan="7" className="text-center">No attendance data recorded for this training yet.</td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}

export default TrainingAttendeeList;
